// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/feature-export/html.ts
// Parameters       : none — this file is byte-identical across the fleet
// Destination      : packages/fleet/export/src/html.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo apunte-digital-app
//     node fleet/fleet.ts check --repo apunte-digital-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
import type { ExportCellValue, ExportDataset } from "./types";

/**
 * PDF export without a heavy dependency: we render the dataset to a small,
 * self-contained, print-styled HTML document. The UI opens it in a new window
 * and calls `print()`, letting the browser's native "Save as PDF" do the work.
 * This keeps the bundle lean and produces a faithful, paginated table.
 */

function escapeHtml(raw: string): string {
  return raw
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function formatCell(value: ExportCellValue): string {
  if (value === null || value === undefined) {
    return "";
  }
  return typeof value === "string" ? value : String(value);
}

/** Renders the dataset to a complete, print-ready HTML document string. */
export function datasetToHtml(dataset: ExportDataset): string {
  const { metadata } = dataset;
  const title = metadata.title ?? "Export";
  const generated = metadata.generatedAt ?? new Date().toISOString();

  const metaRows: string[] = [];
  if (metadata.subtitle) {
    metaRows.push(`<p class="subtitle">${escapeHtml(metadata.subtitle)}</p>`);
  }
  metaRows.push(`<p class="meta">Generated: ${escapeHtml(generated)}</p>`);
  if (metadata.extra) {
    for (const [key, value] of Object.entries(metadata.extra)) {
      metaRows.push(`<p class="meta">${escapeHtml(key)}: ${escapeHtml(value)}</p>`);
    }
  }

  const headerCells = dataset.columns.map((c) => `<th>${escapeHtml(c.header)}</th>`).join("");

  const bodyRows = dataset.rows
    .map(
      (row) => `<tr>${row.map((cell) => `<td>${escapeHtml(formatCell(cell))}</td>`).join("")}</tr>`,
    )
    .join("");

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; color: #111; margin: 2rem; }
  h1 { font-size: 1.5rem; margin: 0 0 0.25rem; }
  .subtitle { color: #444; margin: 0 0 0.25rem; }
  .meta { color: #666; font-size: 0.8rem; margin: 0; }
  table { border-collapse: collapse; width: 100%; margin-top: 1rem; font-size: 0.85rem; }
  th, td { border: 1px solid #ccc; padding: 0.4rem 0.6rem; text-align: left; vertical-align: top; }
  th { background: #f3f4f6; font-weight: 600; }
  tr:nth-child(even) td { background: #fafafa; }
  @media print { body { margin: 0.5rem; } th { background: #eee !important; -webkit-print-color-adjust: exact; } }
</style>
</head>
<body>
  <header>
    <h1>${escapeHtml(title)}</h1>
    ${metaRows.join("\n    ")}
  </header>
  <table>
    <thead><tr>${headerCells}</tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body>
</html>`;
}
